import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveImg } from "@/lib/api";
import { CATEGORY_LABELS, formatDateTime, readingTime } from "@/lib/format";
import { SiteLayout } from "@/components/site/SiteLayout";
import { Kicker } from "@/components/site/BreakingTag";
import { Clock, ArrowLeft, Eye } from "lucide-react";

export default function PreviewArticle() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/admin/articles/${id}`).then((r) => setArticle(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <SiteLayout><div className="py-24 text-center font-body text-muted-foreground">Artikel nicht gefunden.</div></SiteLayout>;
  if (!article) return <SiteLayout><div className="py-32 text-center text-muted-foreground font-body">Lädt…</div></SiteLayout>;

  const statusLabel = article.status === "published"
    ? (article.published_at && new Date(article.published_at) > new Date() ? "Programmiert" : "Veröffentlicht")
    : (article.status === "archived" ? "Archiviert" : "Entwurf");

  return (
    <SiteLayout>
      <div data-testid="preview-banner" className="bg-gold/15 border-b border-gold/50">
        <div className="max-w-[1240px] mx-auto px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
          <span className="inline-flex items-center gap-2 text-sm font-body text-prussian font-semibold">
            <Eye size={16} /> Vorschau · so erscheint der Artikel nach der Veröffentlichung
          </span>
          <span className="flex items-center gap-3 text-xs font-body">
            <span className="uppercase tracking-wide bg-white border border-edborder px-2 py-1 text-prussian">Status: {statusLabel}</span>
            <Link to={`/admin/artikel/${id}`} className="inline-flex items-center gap-1 text-prussian underline"><ArrowLeft size={13} /> Zurück zum Editor</Link>
          </span>
        </div>
      </div>

      <article className="max-w-[760px] mx-auto px-4 py-8">
        <span className="inline-flex items-center gap-1.5 mb-4">
          <Kicker breaking={article.is_breaking}>{article.kicker || CATEGORY_LABELS[article.category]}</Kicker>
        </span>
        <h1 className="font-headline font-black text-prussian text-3xl md:text-[2.75rem] leading-[1.08] tracking-tight">{article.title}</h1>
        <p className="mt-5 font-headline text-xl md:text-2xl text-charcoal/80 leading-relaxed italic">{article.chapo}</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-6 pb-6 border-b border-edborder text-sm font-body text-muted-foreground">
          <span className="font-semibold text-prussian">Von {article.author || "—"}</span>
          <span>·</span>
          <span>{article.published_at ? formatDateTime(article.published_at) : "Datum offen"}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1"><Clock size={13} /> {readingTime(article.body)} Min. Lesezeit</span>
        </div>
        {article.featured_image && (
          <figure className="mt-6">
            <img src={resolveImg(article.featured_image)} alt={article.title} className="w-full object-cover" />
            {(article.image_caption || article.image_credit) && (
              <figcaption className="mt-2 text-xs text-muted-foreground font-body flex justify-between gap-4 border-b border-edborder pb-3">
                <span>{article.image_caption}</span>
                <span className="shrink-0 uppercase tracking-wide">{article.image_credit}</span>
              </figcaption>
            )}
          </figure>
        )}
        <div className="prose-europuls dropcap mt-8" dangerouslySetInnerHTML={{ __html: article.body || "<p><em>Noch kein Text.</em></p>" }} />
        {article.tags?.length > 0 && (
          <div className="mt-10 flex flex-wrap gap-2">
            {article.tags.map((t) => (
              <span key={t} className="text-xs font-body uppercase tracking-wide bg-white border border-edborder text-prussian px-3 py-1">{t}</span>
            ))}
          </div>
        )}
      </article>
    </SiteLayout>
  );
}
